import React from "react";
import { Link } from "react-router-dom";
import { Car, Home, HeartPulse, Building2, Briefcase, Zap, ChevronRight } from "lucide-react";
import { Reveal, SectionHeader } from "./Section";
import { useQuote } from "./QuoteContext";

const services = [
  { icon: Car, title: "Assurance Auto", desc: "Réduisez votre prime grâce à la mise en concurrence.", tag: "AUTO", product: "auto", image: "/images/assurance-auto-comparateur-paris.webp" },
  { icon: Home, title: "Habitation", desc: "Couverture optimale, tarif optimisé.", tag: "HABITATION", product: "habitation" },
  { icon: HeartPulse, title: "Mutuelle Santé", desc: "Une couverture adaptée à votre profil.", tag: "SANTÉ", product: "sante", image: "/images/mutuelle-sante-professionnelle-senior.webp" },
  { icon: Building2, title: "Emprunteur", desc: "Renégociez votre assurance de prêt — Loi Lemoine.", tag: "EMPRUNTEUR", product: "emprunteur", to: "/assurance/emprunteur" },
  { icon: Briefcase, title: "Professionnels", desc: "Multirisque, RC Pro, prévoyance.", tag: "PROFESSIONNEL", product: "rc-pro", to: "/assurance/professionnelle" },
  { icon: Zap, title: "Énergie", desc: "Négociation directe avec les fournisseurs.", tag: "ÉLECTRICITÉ · GAZ", product: "energie" },
];

export default function Services() {
  const { openQuote } = useQuote();
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-brand-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionHeader
          overline="Mes expertises"
          title="Tout ce qu'il faut, sous un même toit."
          subtitle="De l'assurance auto à la négociation d'énergie pour votre entreprise — un courtier indépendant qui défend vos intérêts, pas ceux d'une compagnie."
        />

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {services.map((s, i) => {
            const slug = s.tag
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, "");
            const testId = `service-card-${slug}`;
            const imageTestId = `service-card-image-${slug}`;
            const cardInner = (
              <>
                {s.image && (
                  <div
                    data-testid={imageTestId}
                    className="relative -mt-8 md:-mt-10 -mx-8 md:-mx-10 mb-7 aspect-[16/9] overflow-hidden rounded-t-2xl bg-brand-sand-2"
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="absolute -top-14 -right-14 h-40 w-40 rounded-full bg-brand-gold/5 group-hover:bg-brand-gold/15 transition-colors pointer-events-none" />
                <div className="relative flex items-start justify-between gap-6 mb-10">
                  <div className="h-14 w-14 md:h-16 md:w-16 rounded-full bg-white border border-brand-border text-brand-ink flex items-center justify-center group-hover:bg-brand-ink group-hover:text-white transition-colors shrink-0">
                    <s.icon className="h-6 w-6 md:h-7 md:w-7" strokeWidth={1.4} />
                  </div>
                  <span className="overline mt-3 text-right">{s.tag}</span>
                </div>
                <h3 className="relative font-serif text-2xl md:text-[28px] tracking-tight text-brand-ink leading-snug">{s.title}</h3>
                <p className="relative mt-4 text-brand-ink/65 leading-relaxed text-[15px] md:text-[16px]">{s.desc}</p>
                <div className="relative mt-7 inline-flex items-center gap-1 text-sm font-semibold text-brand-gold">
                  {s.to ? "Découvrir" : "Demander un devis"} <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </>
            );
            const className =
              "card-lift group w-full text-left bg-white rounded-2xl border border-brand-border p-8 md:p-10 relative overflow-hidden";
            return (
              <Reveal key={s.title} delay={i}>
                {s.to ? (
                  <Link to={s.to} data-testid={testId} className={`${className} block`}>
                    {cardInner}
                  </Link>
                ) : (
                  <button
                    data-testid={testId}
                    onClick={() => openQuote(s.product)}
                    className={className}
                  >
                    {cardInner}
                  </button>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
